import React from 'react';
import BookComponent from '../components/Book';
import { Button } from '../components/ui/button';
import { Star, Gift, Heart, ExternalLink } from 'lucide-react';

const BookPage = () => {
  const features = [
    {
      icon: Star,
      title: 'Educational & Fun',
      description: 'Combines learning with creativity through interactive activities and coloring.'
    },
    {
      icon: Gift,
      title: 'Perfect Gift',
      description: 'Ideal for parents, educators, and anyone who wants to empower young learners.'
    },
    {
      icon: Heart,
      title: 'Build Healthy Habits',
      description: 'Teaches children fundamental money management skills from an early age.'
    }
  ];

  const books = [
    {
      title: 'Money Quest: The Ultimate Playbook Teaching You How To Play The Money Game and Win',
      author: 'By Shannon Brooks',
      image: 'https://customer-assets.emergentagent.com/job_sbrooks-expansion/artifacts/eab8pxpo_e7rq9ww-front-shortedge-384%20%281%29.jpg',
      description: 'Money Quest takes young readers on an exciting journey to discover the power of money! Through engaging activities and easy-to-understand lessons, kids will learn the basics of saving, spending wisely, and making smart financial choices. Designed as a fun and interactive coloring book, Money Quest empowers children to build healthy money habits while sparking creativity. Perfect for parents, educators, and young learners eager to embark on a financial adventure!',
      link: 'https://www.lulu.com/shop/shannon-brooks/money-quest-the-ultimate-playbook-teaching-you-how-to-play-the-money-game-and-win/paperback/product-e7rq9ww.html'
    },
    {
      title: 'Life Lessons: Why Insurance Matters',
      author: 'By Shannon Brooks',
      image: 'https://assets.lulu.com/cover_thumbs/p/6/p6gy92k-front-shortedge-384.jpg',
      description: 'Life is unpredictable, but your family\'s security doesn\'t have to be. Life Lessons: Why Insurance Matters breaks down the importance of life insurance in a way that\'s simple, relatable, and eye-opening. Through real-life scenarios and insightful guidance, this book helps you understand how a small decision today can create lasting protection for the ones you love. Whether you\'re just starting your journey or looking to secure your family\'s future, this book will empower you to take action and make informed financial moves that truly matter.',
      link: 'https://www.lulu.com/shop/shannon-brooks/life-lessons-why-insurance-matters/paperback/product-p6gy92k.html'
    }
  ];

  return (
    <div>
      {/* Page Header */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold mb-6">
            Books by Shannon Brooks
          </h1>
          <p className="text-lg md:text-xl text-[#2C3E50] leading-relaxed">
            Empowering readers with essential financial knowledge through engaging and accessible books.
          </p>
        </div>
      </section>

      {/* Books Section */}
      <section className="bg-white py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto space-y-20">
          {books.map((book, index) => (
            <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Book Image */}
              <div className={`flex justify-center ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="rounded-lg overflow-hidden shadow-2xl max-w-sm">
                  <img 
                    src={book.image} 
                    alt={book.title}
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>

              {/* Book Content */}
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <h2 className="text-3xl md:text-4xl font-serif text-[#0A2540] font-bold leading-tight">
                  {book.title}
                </h2>
                <p className="text-base text-[#2C3E50] font-semibold">
                  {book.author}
                </p>
                <p className="text-base md:text-lg text-[#2C3E50] leading-relaxed">
                  {book.description}
                </p>
                <Button 
                  className="bg-[#0B8FD8] hover:bg-[#0973B0] text-white px-8 py-3 text-base rounded-md transition-all duration-300 shadow-lg hover:shadow-xl"
                  onClick={() => window.open(book.link, '_blank')}
                >
                  Buy Now
                  <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>
      
      {/* Book Features Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-6">
            Why These Books Matter
          </h2>
          <p className="text-lg text-[#2C3E50] text-center max-w-3xl mx-auto mb-16">
            Shannon Brooks creates resources that make financial education accessible, engaging, 
            and actionable for readers of all ages.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="text-center space-y-4 p-6">
                  <div className="flex justify-center">
                    <div className="bg-white p-4 rounded-full shadow-lg">
                      <Icon className="h-10 w-10 text-[#0B8FD8]" />
                    </div>
                  </div>
                  <h3 className="text-xl md:text-2xl font-serif text-[#0A2540] font-bold">
                    {feature.title}
                  </h3>
                  <p className="text-base text-[#2C3E50] leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0B8FD8] py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white font-bold mb-6">
            Start Your Financial Education Journey
          </h2>
          <p className="text-lg text-white mb-8">
            These books are perfect for individuals and families looking to build strong financial foundations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              className="bg-white hover:bg-gray-100 text-[#0B8FD8] px-8 py-4 text-lg rounded-md transition-all duration-300 shadow-lg hover:shadow-xl"
              onClick={() => window.open(books[0].link, '_blank')}
            >
              Get Money Quest
              <ExternalLink className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              className="bg-white hover:bg-gray-100 text-[#0B8FD8] px-8 py-4 text-lg rounded-md transition-all duration-300 shadow-lg hover:shadow-xl"
              onClick={() => window.open(books[1].link, '_blank')}
            >
              Get Life Lessons
              <ExternalLink className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookPage;
